import React from 'react';

import { Link } from '@gogame/components';
import { DataGrid } from '@gogame/data-grid';
import { useChildComponent } from '@gogame/hooks-ts';
import {
  useScreenGo,
  useScreenState,
  useScreenDispatch,
} from '@gogame/screens';
import MasterCampaignsActions from 'scenes/Influencers/MasterCampaigns/components/Actions';
import MasterCampaignsToolbar from './MasterCampaignsToolbar';
import {
  MasterCampaignFormEdit,
  MasterCampaignFormDuplicate,
  MasterCampaignFormCreate,
} from 'scenes/Influencers/MasterCampaigns/components/MasterCampaignForm';
import {
  COLUMN_ID,
  COLUMN_PERIOD,
  COLUMN_NAME,
  COLUMN_APPLICATION,
  COLUMN_NETWORK,
  COLUMN_PLATFORMS,
  COLUMN_BRIEFS,
  COLUMN_TOTAL_BUDGET,
  COLUMN_COST_MODELS,
  COLUMN_AGENCIES,
  COLUMN_INFLUENCER_CHANNELS,
  COLUMN_INFLUENCER_CAMPAIGNS,
  COLUMN_LAST_UPDATED_BY,
  COLUMN_LAST_UPDATED_DATE,
  COLUMN_CREATED_BY,
  COLUMN_CREATED_DATE,
} from 'scenes/Influencers/MasterCampaigns/constants';

import { useLoadMasterCampaignTable } from 'scenes/Influencers/MasterCampaigns/data';
import {
  SET_TABLE_SETTINGS,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/Influencers/MasterCampaigns/screenReducer';
import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import { MasterCampaignDetailsLink } from './MasterCampaignDetailsLink';
import { InfluencerCampaignsLink } from './InfluencerCampaignsLink';
import { InfluencerChannelsLink } from './InfluencerChannelsLink';

const MasterCampaignsDataTable = () => {
  const screenState = useScreenState();
  const dispatch = useScreenDispatch();
  const go = useScreenGo();
  const [childComponent, openChildComponent] = useChildComponent();
  const { filters, tableSettings, searchQuery } = screenState;

  const { data, isLoading, isRefetching } = useLoadMasterCampaignTable(
    {
      filters,
      searchQuery,
      tableSettings,
    },
    {
      enabled: screenState.ready,
    }
  );

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleEdit = (_e, { id }) => {
    openChildComponent(MasterCampaignFormEdit, {
      id,
    });
  };

  const handleDuplicate = (_e, { id }) => {
    openChildComponent(MasterCampaignFormDuplicate, {
      id,
      onSubmit: async ({ id }) => {
        go(id);
      },
    });
  };

  const handleOpenAgency = (id) => {
    openChildComponent(AffiliateDetails, {
      id,
    });
  };

  const handleCreate = (provider) => {
    openChildComponent(MasterCampaignFormCreate, {
      attributionProviderId: provider,
      onSubmit: (response) => {
        go(response.id);
      },
    });
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        loading={isLoading}
        fetching={isRefetching}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        ready={screenState.ready}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<MasterCampaignsToolbar onCreate={handleCreate} />}
        storageKey={'influencersMasterCampaigns'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          align={'right'}
          headerAlign={'right'}
          width={75}
          fixed={true}
          resizable={false}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          headerName={'Name'}
          type={'link'}
          width={256}
          fixed={true}
          resizable={false}
          linkComponent={MasterCampaignDetailsLink}
        />
        <DataGrid.Column
          field={COLUMN_PERIOD}
          headerName={'Period'}
          type={'date'}
          template={'MONTH'}
          timeZone={'utc'}
          placeholder={'—'}
          width={92}
          fixed={true}
          resizable={false}
        />
        <DataGrid.Column
          field={COLUMN_APPLICATION}
          headerName={'Application'}
          type={'text'}
          width={160}
        />
        <DataGrid.Column
          field={COLUMN_NETWORK}
          headerName={'Network'}
          type={'text'}
          width={148}
        />
        <DataGrid.Column
          field={COLUMN_PLATFORMS}
          headerName={'Platforms'}
          type={'commaList'}
          sortable={false}
          width={156}
          getItemValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_BRIEFS}
          headerName={'Briefs'}
          type={'list'}
          width={156}
          sortable={false}
          getItemValue={({ name, attachmentUrl }) => (
            <Link href={attachmentUrl} target={'_blank'}>
              {name}
            </Link>
          )}
        />
        <DataGrid.Column
          field={COLUMN_TOTAL_BUDGET}
          headerName={'Total Budget'}
          type={'currency'}
          align={'right'}
          headerAlign={'right'}
          width={112}
        />
        <DataGrid.Column
          field={COLUMN_COST_MODELS}
          headerName={'Cost Models'}
          type={'commaList'}
          width={124}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_AGENCIES}
          headerName={'Agencies'}
          limit={1}
          type={'list'}
          sortable={false}
          getItemValue={({ id, name }) => (
            <Link onClick={() => handleOpenAgency(id)}>{name}</Link>
          )}
          width={188}
        />
        <DataGrid.Column
          field={COLUMN_INFLUENCER_CHANNELS}
          headerName={'Influencers Channels'}
          type={'link'}
          width={124}
          sortable={false}
          linkComponent={InfluencerChannelsLink}
        />
        <DataGrid.Column
          field={COLUMN_INFLUENCER_CAMPAIGNS}
          headerName={'Influencers Campaigns'}
          type={'link'}
          sortable={false}
          linkComponent={InfluencerCampaignsLink}
          width={124}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED_BY}
          headerName={'Last Updated by'}
          type={'text'}
          width={150}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED_DATE}
          headerName={'Last Updated'}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
          width={150}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          type={'text'}
          width={150}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
          width={150}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          actionsComponent={MasterCampaignsActions}
          limit={3}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default MasterCampaignsDataTable;
